import { __rest } from '../../../node_modules/tslib/tslib.es6.js';
import { styled, useTheme, Box } from '@mui/material';
import React from 'react';
import { CampaignType } from '../../../types/Campaign.js';
import '../../../types/filters.js';
import '../../../types/product.js';
import '../../../types/RewardAmountType.js';
import { getImageAssetUrl } from '../../../utils/assetUtils.js';
import { formatMoney } from '../../../utils/stringUtils.js';

const StyledContainer = styled('div', {
    shouldForwardProp: (prop) => !['rewardCampaignType', 'clickable'].includes(prop.toString()),
})(({ clickable, theme }) => ({
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    columnGap: 7,
    '*': {
        mixBlendMode: 'unset !important',
    },
    '.reward-pill': {
        textWrap: 'nowrap',
        borderRadius: 25,
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: 0,
        display: 'flex',
        alignItems: 'center',
        ':hover': {
            cursor: clickable ? 'pointer' : 'default',
        },
    },
    '.rewardPercent-text': {
        color: theme.vars.palette.text.primary,
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: 0.24,
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
    },
}));
// default styles are from pdp buy-box
const getConfigByRewardCampaignType = (rewardCampaignType, theme) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    switch (rewardCampaignType) {
        case CampaignType.DailyDealsRewardCampaign:
            return {
                iconImgProps: {
                    src: getImageAssetUrl('instant-reward-icon.webp'),
                    alt: 'instant-reward-icon',
                },
                rewardTypeDisplay: 'instant',
                containerStyle: {
                    color: (_a = theme.vars.globalComponentPresets) === null || _a === void 0 ? void 0 : _a.pill.dailyDeal.contrastText,
                    backgroundColor: (_b = theme.vars.globalComponentPresets) === null || _b === void 0 ? void 0 : _b.pill.dailyDeal.main,
                },
            };
        case CampaignType.PurchaseRewardCampaign:
            return {
                iconImgProps: {
                    src: getImageAssetUrl('purchase-reward-icon.webp'),
                    alt: 'purchase-reward-icon',
                },
                rewardTypeDisplay: 'purchase',
                containerStyle: {
                    color: (_c = theme.vars.globalComponentPresets) === null || _c === void 0 ? void 0 : _c.pill.purchase.contrastText,
                    backgroundColor: (_d = theme.vars.globalComponentPresets) === null || _d === void 0 ? void 0 : _d.pill.purchase.main,
                },
            };
        case CampaignType.AffiliateRewardCampaign:
            return {
                iconImgProps: {
                    src: getImageAssetUrl('affiliate-reward-icon.webp'),
                    alt: 'affiliate-reward-icon',
                },
                rewardTypeDisplay: '',
                containerStyle: {
                    color: (_e = theme.vars.globalComponentPresets) === null || _e === void 0 ? void 0 : _e.pill.affiliate.contrastText,
                    backgroundColor: (_f = theme.vars.globalComponentPresets) === null || _f === void 0 ? void 0 : _f.pill.affiliate.main,
                },
            };
        case CampaignType.ContentRewardCampaign:
            return {
                iconImgProps: {
                    src: getImageAssetUrl('content-reward-icon.webp'),
                    alt: 'content-reward-icon',
                },
                rewardTypeDisplay: '',
                containerStyle: {
                    color: (_g = theme.vars.globalComponentPresets) === null || _g === void 0 ? void 0 : _g.pill.content.contrastText,
                    backgroundColor: (_h = theme.vars.globalComponentPresets) === null || _h === void 0 ? void 0 : _h.pill.content.main,
                },
            };
        case CampaignType.SurveyRewardCampaign:
            return {
                iconImgProps: {
                    src: getImageAssetUrl('survey-reward-icon.webp'),
                    alt: 'survey-reward-icon',
                },
                rewardTypeDisplay: '',
                containerStyle: {
                    color: (_j = theme.vars.globalComponentPresets) === null || _j === void 0 ? void 0 : _j.pill.survey.contrastText,
                    backgroundColor: (_k = theme.vars.globalComponentPresets) === null || _k === void 0 ? void 0 : _k.pill.survey.main,
                },
            };
        default:
            return {
                iconImgProps: {
                    src: undefined,
                    alt: 'affiliate-reward-icon',
                },
                rewardTypeDisplay: '',
                containerStyle: {
                    color: (_l = theme.vars.globalComponentPresets) === null || _l === void 0 ? void 0 : _l.pill.default.contrastText,
                    backgroundColor: (_m = theme.vars.globalComponentPresets) === null || _m === void 0 ? void 0 : _m.pill.default.main,
                },
            };
    }
};
const RewardPillWithText = ({ rewardAmount, rewardCampaignType, rewardPercent, rewardPercentText, rewardPercentTextOverride, containerStyle: containerStyleOverride = {}, pillContainerStyle = {}, baseFontSize = 14, onPillClick, className, customPillText, }) => {
    const theme = useTheme();
    const { iconImgProps, rewardTypeDisplay, containerStyle } = getConfigByRewardCampaignType(rewardCampaignType, theme);
    const restIconImgProps = __rest(iconImgProps, []);
    const pillDefaultPadding = {
        paddingTop: `${baseFontSize / 3}px`,
        paddingLeft: `${baseFontSize * 0.95}px`,
        paddingBottom: `${baseFontSize / 3}px`,
        paddingRight: `${baseFontSize * 0.95}px`,
    };
    const subfixText = rewardPercentTextOverride
        ? rewardPercentTextOverride
        : rewardPercent
            ? Math.round(rewardPercent) + '% ' + (rewardPercentText !== null && rewardPercentText !== void 0 ? rewardPercentText : `${rewardTypeDisplay} reward`)
            : null;
    const pillText = customPillText !== null && customPillText !== void 0 ? customPillText : formatMoney(rewardAmount);
    return (React.createElement(StyledContainer, { rewardCampaignType: rewardCampaignType, style: Object.assign({ visibility: rewardAmount || customPillText ? 'visible' : 'hidden', display: 'flex' }, containerStyleOverride), clickable: !!onPillClick, className: className },
        React.createElement(Box, { sx: { borderRadius: '999px', backgroundColor: '#fff', display: 'flex' } },
            React.createElement(Box, { className: "reward-pill", sx: Object.assign(Object.assign(Object.assign(Object.assign({}, containerStyle), pillDefaultPadding), pillContainerStyle), { fontWeight: 600, fontFamily: 'inherit' }), onClick: onPillClick },
                iconImgProps.src && !customPillText ? (React.createElement("img", Object.assign({}, restIconImgProps, { style: {
                        maxWidth: baseFontSize,
                        maxHeight: baseFontSize,
                        objectFit: 'contain',
                        marginRight: baseFontSize * 0.416,
                        verticalAlign: 'middle',
                    } }))) : null,
                React.createElement(Box, { component: "span", sx: {
                        lineHeight: '1.3em',
                        margin: 0,
                        padding: 0,
                    } }, pillText))),
        subfixText ? (React.createElement("div", { className: "rewardPercent-text", style: {
                fontWeight: 600,
                fontFamily: 'inherit',
            } }, subfixText)) : null));
};

export { RewardPillWithText };
//# sourceMappingURL=RewardPillWithText.js.map
